import { apiClient } from 'shared/api/client';
import { endpoints } from 'shared/api/endpoints';
import { fetchWithFallback } from 'shared/data/fetchWithFallback';
import { mapDbLesson } from 'shared/data/mappers';
import { getSupabase } from 'shared/supabase/client';
import type { Lesson } from 'types';

async function canOpenLesson(lessonId: string): Promise<boolean> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('stud_can_open_lesson', { p_lesson_id: lessonId });
  // Keep backward compatibility for environments where phase-7 migration isn't applied yet.
  if (error && /function .*stud_can_open_lesson/i.test(error.message)) return true;
  if (error) throw error;
  return Boolean(data);
}

export async function fetchLessonById(id: string): Promise<Lesson> {
  return fetchWithFallback(
    async () => {
      const allowed = await canOpenLesson(id);
      if (!allowed) {
        throw new Error('lesson_progression_locked');
      }

      const { data: lesson, error } = await getSupabase()
        .from('inst_lessons')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();
      if (error) throw error;

      const { data: video } = await getSupabase()
        .from('inst_videos')
        .select('url, provider, visibility')
        .eq('lesson_id', id)
        .eq('is_published', true)
        .is('deleted_at', null)
        .limit(1)
        .maybeSingle();

      return mapDbLesson({
        ...lesson,
        video_url: video?.url,
        video_provider: video?.provider,
        video_visibility: video?.visibility,
      });
    },
    async () => {
      const { data } = await apiClient.get<Lesson>(endpoints.lessons.detail(id));
      return data;
    }
  );
}

export async function fetchRelatedLessons(id: string): Promise<Lesson[]> {
  return fetchWithFallback(
    async () => {
      const { data: current } = await getSupabase()
        .from('inst_lessons')
        .select('course_id')
        .eq('id', id)
        .single();

      if (!current) return [];

      const { data, error } = await getSupabase()
        .from('inst_lessons')
        .select('*')
        .eq('course_id', current.course_id)
        .neq('id', id)
        .is('deleted_at', null)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []).map((row) => mapDbLesson(row));
    },
    async () => {
      const { data } = await apiClient.get<Lesson[]>(endpoints.lessons.related(id));
      return data;
    }
  );
}
